import React, { createContext, useContext } from 'react'
import axios from 'axios'
import { useAuth } from '@clerk/clerk-react'
import { toast } from 'react-toastify'

const AxiosContext = createContext(axios.create())

export const AxiosProvider = ({ children }) => {
  const { getToken } = useAuth()

  // Normalize server base URL to avoid double "/api" (e.g., base ends with /api and request starts with /api)
  const rawBase = (process.env.REACT_APP_SERVER_URL || '').replace(/\/+$/, '')
  const normalizedBase = rawBase.replace(/\/?api$/i, '')

  const axiosInstance = axios.create({
    baseURL: normalizedBase || undefined,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        const token = await getToken()
        if (token) config.headers.Authorization = `Bearer ${token}`
      } catch (error) {
        // no-op
      }
      return config
    },
    (error) => {
      if (process.env.NODE_ENV !== 'production') toast.error('An error occurred while preparing the request.')
      return Promise.reject(error)
    },
  )

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response) {
        if (process.env.NODE_ENV !== 'production') {
          if (error.response.status === 401)
            toast.error(
              error.response.data?.message || `Authentication failed: Please log in to continue`,
            )
          else
            toast.error(
              error.response.data?.message ||
                `Error: ${error.response.status} - Something went wrong.`,
            )
        }
      } else if (error.request) {
        if (process.env.NODE_ENV !== 'production') toast.error('No response from the server. Please try again.')
      } else {
        // no-op
        if (process.env.NODE_ENV !== 'production') toast.error('An unexpected error occurred. Please try again.')
      }
      return Promise.reject(error)
    },
  )

  return <AxiosContext.Provider value={axiosInstance}>{children}</AxiosContext.Provider>
}

export const useAxios = () => useContext(AxiosContext)
